import { useEffect, useState } from 'react';
import { DataTable } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/input';
import { PaginationControls } from '@/components/ui/pagination';
import { Spinner } from '@/components/ui/spinner';
import { useToolExecutionLogs } from '@/hooks';
import type { ToolExecutionLog } from '@/services';
import { getToolExecutionLogsColumns } from './tool-execution-logs-columns';

interface ToolExecutionLogsSectionProps {
	provider: string;
	tool: string;
	onRowClick: (log: ToolExecutionLog) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

/** The account's latest recorded calls of this action, newest first, searchable by request ID. */
export function ToolExecutionLogsSection({ provider, tool, onRowClick }: ToolExecutionLogsSectionProps) {
	const [search, setSearch] = useState('');
	const [requestId, setRequestId] = useState('');
	const [page, setPage] = useState(1);
	const { data, isLoading, isError } = useToolExecutionLogs(provider, tool, requestId, page);
	const logs = data?.logs ?? [];

	useEffect(() => {
		const timer = setTimeout(() => {
			setRequestId(search.trim());
			setPage(1);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [search]);

	return (
		<section className='flex flex-col gap-3'>
			<div className='flex'>
				<SearchInput
					placeholder='Search by request ID…'
					aria-label='Search by request ID'
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					className='h-8 w-[280px]'
				/>
			</div>
			<DataTable
				columns={getToolExecutionLogsColumns()}
				data={logs}
				isLoading={isLoading}
				loadingContent={<Spinner className='mx-auto size-5 text-gray-700' />}
				enableSorting={true}
				enablePagination={false}
				enableColumnVisibility={false}
				enableRowSelection={false}
				emptyMessage={isError ? 'Failed to load event logs.' : requestId ? 'No calls with this request ID.' : 'No calls recorded yet.'}
				onRowClick={onRowClick}
			/>
			{data && (
				<PaginationControls page={data.pagination.page} totalPages={data.pagination.totalPages} onPageChange={setPage} />
			)}
		</section>
	);
}
